import classNames from 'classnames';
import React, { ReactElement, useContext } from 'react';

import { ArrowAngleRight } from '@zooplus/icons';

import AppContext from '../../context';
import BonusPointsBanner from '../BonusPoints/BonusPointsBanner';
import { ThemedIcon } from '../Icon/Icon';

import BurgerBanner from './BurgerBanner';
import styles from './BurgerNavigation.module.scss';
import { Item, PanelData } from './types';

export interface BurgerPanelProps {
    onClickNextPanel: (nextPanelPath: string) => void;
    openPanels: PanelData[];
}

export default function BurgerPanel({
    onClickNextPanel,
    openPanels,
}: BurgerPanelProps): ReactElement {
    const {
        translations,
        isLoggedIn,
        bonuspointsAvailable,
        customerStatusLoaded,
    } = useContext(AppContext);

    const accessibleLabel = translations.get(
        'shop.header.main.navigation.category.accessible.button.label',
    );
    const { currentPath } = openPanels.slice(-1)[0];

    return (
        <>
            {openPanels.map((panel: PanelData, index: number) => (
                <ul
                    className={classNames(
                        styles.burgerPanelList,
                        styles.scrollBox,
                    )}
                    key={panel.currentPath}
                    id={panel.trackingId}>
                    {index === openPanels.length - 1 && (
                        <BurgerBanner openPanels={openPanels} />
                    )}
                    {index === 0 &&
                        customerStatusLoaded &&
                        isLoggedIn &&
                        bonuspointsAvailable && <BonusPointsBanner />}
                    {panel.items.map(
                        ({
                            path,
                            title,
                            hasChildren,
                            isSpecial,
                            highlighted,
                            active,
                            isParent: isParentLink,
                            isBold,
                            isSeperator,
                        }: Item) => {
                            const isDisabled =
                                currentPath !== panel.currentPath;
                            const isHighlighted = isSpecial || highlighted;
                            const itemLink =
                                currentPath === panel.currentPath
                                    ? path
                                    : undefined;
                            if (!hasChildren) {
                                return (
                                    <li
                                        key={path}
                                        data-zta={
                                            isParentLink
                                                ? 'burger-nav-item-parent'
                                                : 'burger-nav-item-no-child'
                                        }
                                        className={classNames(
                                            styles.burgerItem,
                                            {
                                                [styles.burgerItemActive]:
                                                    active,
                                                [styles.burgerItemParentLink]:
                                                    isParentLink,
                                                [styles.burgerItemBold]: isBold,

                                                [styles.burgerItemSeperator]:
                                                    isSeperator,
                                            },
                                        )}>
                                        <a
                                            href={itemLink}
                                            data-zta="burger-nav-item-link"
                                            className={classNames(
                                                styles.burgerItemLink,
                                                {
                                                    [styles.burgerItemLinkSpecial]:
                                                        isHighlighted,
                                                },
                                            )}>
                                            <span>{title}</span>
                                        </a>
                                    </li>
                                );
                            }

                            return (
                                <li
                                    key={path}
                                    data-zta="burger-nav-item-with-children"
                                    className={classNames(styles.burgerItem, {
                                        [styles.burgerItemActive]: active,
                                        [styles.burgerItemBold]: isBold,
                                        [styles.burgerItemSeperator]:
                                            isSeperator,
                                    })}>
                                    <button
                                        type="button"
                                        data-zta="burger-nav-item-btn"
                                        className={styles.burgerItemButton}
                                        onClick={() => onClickNextPanel(path)}
                                        disabled={isDisabled}>
                                        <span>{title}</span>
                                        <ThemedIcon
                                            color="neutralDark"
                                            Icon={ArrowAngleRight}
                                            aria-label={accessibleLabel}
                                            data-zta="angleRightIcon"
                                        />
                                    </button>
                                </li>
                            );
                        },
                    )}
                </ul>
            ))}
        </>
    );
}
